import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { type ReservationBooking } from '../services/reservation-system/reservation-board.service';
import { useAuth } from '../hooks/useAuth';
import { getErrorResponseStatus } from '../utils/error.utils';
import {
  loadHolidayCountryCode,
  saveHolidayCountryCode,
} from '../utils/reservationBoardFormPrefs.util';
import { RS_DEFAULT_COUNTRY_CODE, isRsCountryCode, type RsCountryCode } from '../constants/rsCountryCodes';
import RsBoardCalendarChrome from '../components/reservation-system/RsBoardCalendarChrome';
import RsBoardCalendarModals from '../components/reservation-system/RsBoardCalendarModals';
import RsBoardRouteStates from '../components/reservation-system/RsBoardRouteStates';
import RsBoardSelectionOutsideMonth from '../components/reservation-system/RsBoardSelectionOutsideMonth';
import RsBookingSidebarForm, {
  RS_BOOKING_DISPLAY_NAME_INPUT_ID,
} from '../components/reservation-system/RsBookingSidebarForm';
import RsCalendarGrid from '../components/reservation-system/RsCalendarGrid';
import styles from '../components/reservation-system/ReservationBoardCalendar.module.css';
import {
  buildRsBookingSpanGroups,
  createDefaultRsBookingRange,
  createRsBookingRangeAfterBookingEnd,
  createRsBookingRangeForCalendarDay,
  getRsBookingDurationMs,
  getRsDaysInMonth,
  getRsSelectedDayInMonth,
  isRsLocalDateTimeInMonth,
  afterRsLayoutPaint,
  getRsBoardLayoutEl,
  getRsMobileSubmitBarInsetPx,
  listRsDraftOverlapResourceNames,
  parseRsLocalDateYmd,
  RS_NARROW_BOARD_MQ,
  rsCalendarGridColumns,
  scheduleScrollRsCalendarDayIntoView,
  revealRsBookingForm,
} from '../utils/reservationBoardCalendar.util';
import { useRsCalendarStickyOffsets } from '../hooks/useRsCalendarStickyOffsets';
import { useRsBoardCalendarMonth } from '../hooks/useRsBoardCalendarMonth';
import { useRsBoardShareMutations } from '../hooks/useRsBoardShareMutations';
import { useRsBoardShareQueries } from '../hooks/useRsBoardShareQueries';
import { useRsBoardResourceAdmin } from '../hooks/useRsBoardResourceAdmin';
import { useRsBoardFormPrefs } from '../hooks/useRsBoardFormPrefs';
import { useRsFlashMessage } from '../hooks/useRsFlashMessage';
import { useRsBoardDocumentTitle } from '../hooks/useRsBoardDocumentTitle';
import { useRsBoardConfirmActions } from '../hooks/useRsBoardConfirmActions';

export default function ReservationBoardCalendar() {
  const { shareToken: shareTokenParam } = useParams<{ shareToken: string }>();
  const shareToken = shareTokenParam?.trim() ?? '';
  const routeValid = shareToken.length > 0;
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    today,
    year,
    month,
    shiftMonth,
    goToCurrentMonth,
    goToLocalDateTime,
    isViewingCurrentMonth,
  } = useRsBoardCalendarMonth();
  const { message, error, flashSuccess, flashError, clearFlash } = useRsFlashMessage();

  const defaultBookingRange = useMemo(() => createDefaultRsBookingRange(), []);
  const [startDateTime, setStartDateTime] = useState(defaultBookingRange.start);
  const [endDateTime, setEndDateTime] = useState(defaultBookingRange.end);
  const [selectedResourceNames, setSelectedResourceNames] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  const [editResourceIndex, setEditResourceIndex] = useState<number | null>(null);
  const [editResourceName, setEditResourceName] = useState('');
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ReservationBooking | null>(null);
  const [deletePasswordInput, setDeletePasswordInput] = useState('');
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [holidayCountryCode, setHolidayCountryCode] = useState<RsCountryCode>(RS_DEFAULT_COUNTRY_CODE);
  /** 登録直後は次枠下書きをカレンダーに出さない（確定カード横の「登録前」幽霊を防ぐ） */
  const [showCalendarDraft, setShowCalendarDraft] = useState(true);

  const setStartWithDraft = useCallback((value: string) => {
    setShowCalendarDraft(true);
    setStartDateTime(value);
  }, []);
  const setEndWithDraft = useCallback((value: string) => {
    setShowCalendarDraft(true);
    setEndDateTime(value);
  }, []);
  const setResourcesWithDraft = useCallback((value: string[]) => {
    setShowCalendarDraft(true);
    setSelectedResourceNames(value);
  }, []);

  /** 名前未入力時だけフォーム主視野へ。入力済みならカレンダーを残し登録バーで確定できる */
  const revealFormIfNameEmpty = useCallback(() => {
    if (displayName.trim()) return;
    revealRsBookingForm({
      focusNameInputId: RS_BOOKING_DISPLAY_NAME_INPUT_ID,
      focusNameIfEmpty: true,
      displayName,
    });
  }, [displayName]);

  const { boardQuery, bookingsQuery, holidaysQuery } = useRsBoardShareQueries(
    shareToken,
    year,
    month,
    holidayCountryCode,
    routeValid
  );

  const board = boardQuery.data;
  const bookings = bookingsQuery.data ?? [];
  const isBoardOwner = Boolean(user?.id && board?.userId === user.id);
  const calendarColumnRef = useRef<HTMLDivElement>(null);
  useRsCalendarStickyOffsets(
    calendarColumnRef,
    `${board?.id ?? ''}:${shareToken}:${isBoardOwner ? 1 : 0}`,
    { enableScrollFormFocus: !displayName.trim() }
  );
  const boardId = board?.id ?? 0;

  const { persistFormPrefs } = useRsBoardFormPrefs(board?.id, board?.resourceNames, {
    displayName,
    notes,
    selectedResourceNames,
    setDisplayName,
    setNotes,
    setSelectedResourceNames,
  });

  const holidayByDay = useMemo(() => {
    const map = new Map<number, string>();
    for (const entry of holidaysQuery.data ?? []) {
      map.set(entry.day, entry.name);
    }
    return map;
  }, [holidaysQuery.data]);

  useEffect(() => {
    if (!board) return;
    const boardCountry = isRsCountryCode(board.countryCode) ? board.countryCode : RS_DEFAULT_COUNTRY_CODE;
    if (user?.id && board.userId === user.id) {
      setHolidayCountryCode(boardCountry);
      return;
    }
    const stored = loadHolidayCountryCode(board.id);
    const next = stored && isRsCountryCode(stored) ? stored : boardCountry;
    setHolidayCountryCode(next);
  }, [board?.id, board?.countryCode, user?.id]);

  // 狭い画面の固定登録バー分を scrollIntoView / フォーカス移動の余白に反映
  useEffect(() => {
    if (!board) return;
    const root = document.documentElement;
    const mq = window.matchMedia(RS_NARROW_BOARD_MQ);
    const sync = () => {
      const layout = getRsBoardLayoutEl();
      if (!mq.matches) {
        root.style.removeProperty('scroll-padding-bottom');
        layout?.style.removeProperty('--rs-mobile-submit-clearance');
        return;
      }
      const inset = getRsMobileSubmitBarInsetPx();
      if (inset > 0) {
        const clearancePx = inset + 12;
        root.style.scrollPaddingBottom = `${clearancePx}px`;
        layout?.style.setProperty('--rs-mobile-submit-clearance', `${clearancePx}px`);
      } else {
        root.style.removeProperty('scroll-padding-bottom');
        layout?.style.removeProperty('--rs-mobile-submit-clearance');
      }
    };
    sync();
    afterRsLayoutPaint(sync);
    window.addEventListener('resize', sync);
    mq.addEventListener('change', sync);
    const ro = new ResizeObserver(sync);
    const layout = getRsBoardLayoutEl();
    if (layout) ro.observe(layout);
    const bar = document.querySelector('[data-rs-submit-bar]');
    if (bar) ro.observe(bar);
    return () => {
      window.removeEventListener('resize', sync);
      mq.removeEventListener('change', sync);
      ro.disconnect();
      root.style.removeProperty('scroll-padding-bottom');
      getRsBoardLayoutEl()?.style.removeProperty('--rs-mobile-submit-clearance');
    };
  }, [board?.id]);

  const daysInMonth = useMemo(() => getRsDaysInMonth(year, month), [year, month]);

  const bookingSpanGroups = useMemo(
    () => (board ? buildRsBookingSpanGroups(bookings, board.resourceNames, year, month, daysInMonth) : []),
    [bookings, board, daysInMonth, year, month]
  );

  const selectedDay = useMemo(
    () => getRsSelectedDayInMonth(startDateTime, year, month),
    [startDateTime, year, month]
  );

  const draftOverlapResourceNames = useMemo(
    () =>
      listRsDraftOverlapResourceNames(
        startDateTime,
        endDateTime,
        selectedResourceNames,
        bookings
      ),
    [startDateTime, endDateTime, selectedResourceNames, bookings]
  );

  const handlePickCalendarSlot = useCallback(
    (day: number, resourceName?: string) => {
      const range = createRsBookingRangeForCalendarDay(
        year,
        month,
        day,
        startDateTime,
        endDateTime
      );
      setStartWithDraft(range.start);
      setEndWithDraft(range.end);
      // 項目マスをクリックしたら「その項目だけ」を選ぶ（他項目が残ると意図せず複数予約になる）
      // 日／曜だけのクリックでは項目選択は変えない（複数選択のまま日時だけ更新）
      if (resourceName) {
        const next = [resourceName];
        setResourcesWithDraft(next);
        persistFormPrefs({ resourceNames: next });
      }
      revealFormIfNameEmpty();
    },
    [
      year,
      month,
      startDateTime,
      endDateTime,
      persistFormPrefs,
      setStartWithDraft,
      setEndWithDraft,
      setResourcesWithDraft,
      revealFormIfNameEmpty,
    ]
  );

  /** 既存予約クリック: その項目＋終了直後の枠をフォームへ（続けて取る導線） */
  const handlePrefillAfterBooking = useCallback(
    (booking: ReservationBooking) => {
      const range = createRsBookingRangeAfterBookingEnd(
        booking.endTime,
        getRsBookingDurationMs(startDateTime, endDateTime)
      );
      goToLocalDateTime(range.start);
      setStartWithDraft(range.start);
      setEndWithDraft(range.end);
      const next = [booking.resourceName];
      setResourcesWithDraft(next);
      persistFormPrefs({ resourceNames: next });
      revealFormIfNameEmpty();
    },
    [
      startDateTime,
      endDateTime,
      goToLocalDateTime,
      persistFormPrefs,
      setStartWithDraft,
      setEndWithDraft,
      setResourcesWithDraft,
      revealFormIfNameEmpty,
    ]
  );

  const {
    createMutation,
    deleteMutation,
    issueShareMutation,
    revokeShareMutation,
    updateResourceMutation,
    updateCountryMutation,
  } = useRsBoardShareMutations({
    shareToken,
    boardId,
    board,
    t,
    flash: { flashSuccess, flashError, clearFlash },
    form: {
      selectedResourceNames,
      startDateTime,
      endDateTime,
      displayName,
      notes,
      deletePassword,
      setStartDateTime: setStartWithDraft,
      setEndDateTime: setEndWithDraft,
      setDeletePassword,
      setSelectedResourceNames: setResourcesWithDraft,
      persistFormPrefs,
    },
    resourceEdit: {
      editResourceIndex,
      setEditResourceIndex,
      setEditResourceName,
    },
    deleteModal: {
      setDeleteTarget,
      setDeletePasswordInput,
      setShowOverlapModal,
      setDeleteErrorMessage,
    },
    onAfterCreate: () => setShowCalendarDraft(false),
  });

  const handleHolidayCountryChange = (code: RsCountryCode) => {
    setHolidayCountryCode(code);
    if (isBoardOwner) {
      updateCountryMutation.mutate(code);
      return;
    }
    if (board?.id) {
      saveHolidayCountryCode(board.id, code);
    }
  };

  const { handleIssueShare, handleRevokeShare, requestDeleteBooking } = useRsBoardConfirmActions({
    t,
    hasShareUrl: Boolean(board?.shareUrl),
    issueShare: () => issueShareMutation.mutate(),
    revokeShare: () => revokeShareMutation.mutate(),
    openDeleteModal: (booking) => {
      setDeleteTarget(booking);
      setDeletePasswordInput('');
      setDeleteErrorMessage(null);
    },
  });

  const submitDeleteBooking = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate({
      bookingId: deleteTarget.id,
      password: deletePasswordInput.trim() || undefined,
    });
  };

  const { openResourceEdit, submitResourceEdit, addResource, removeResource } =
    useRsBoardResourceAdmin({
      board,
      bookings,
      editResourceIndex,
      editResourceName,
      setEditResourceIndex,
      setEditResourceName,
      updateResourceMutation,
      flashError,
      t,
    });

  useRsBoardDocumentTitle(board?.name, shareToken, t);

  const revealSelectionMonth = useCallback(() => {
    goToLocalDateTime(startDateTime);
    const parts = parseRsLocalDateYmd(startDateTime.slice(0, 10));
    if (!parts) return;
    scheduleScrollRsCalendarDayIntoView(parts.day, { behavior: 'smooth' });
  }, [goToLocalDateTime, startDateTime]);

  if (!routeValid) {
    return <RsBoardRouteStates kind="invalid" t={t} />;
  }

  if (boardQuery.isLoading) {
    return <RsBoardRouteStates kind="loading" />;
  }

  if (boardQuery.isError || !board) {
    return (
      <RsBoardRouteStates
        kind="error"
        t={t}
        notFound={getErrorResponseStatus(boardQuery.error) === 404}
        showBack={Boolean(user)}
        onBack={() => navigate('/reservations')}
      />
    );
  }

  const gridStyle = rsCalendarGridColumns(board.resourceNames.length, isBoardOwner);
  const selectionInViewMonth = isRsLocalDateTimeInMonth(startDateTime, year, month);

  return (
    <Container fluid className="py-2">
      {(message || error) && (
        <Alert
          variant={error ? 'danger' : 'success'}
          dismissible
          className={styles.flashAlert}
          onClose={clearFlash}
        >
          {error || message}
        </Alert>
      )}

      <div className={styles.reservationBoardLayout} data-rs-board-layout>
        <div className={styles.calendarColumn} ref={calendarColumnRef}>
          <RsBoardCalendarChrome
            boardId={board.id}
            boardName={board.name}
            isBoardOwner={isBoardOwner}
            year={year}
            month={month}
            isViewingCurrentMonth={isViewingCurrentMonth}
            onPrevMonth={() => shiftMonth(-1)}
            onToday={goToCurrentMonth}
            onNextMonth={() => shiftMonth(1)}
            holidayCountryCode={holidayCountryCode}
            onHolidayCountryChange={handleHolidayCountryChange}
            countryUpdatePending={updateCountryMutation.isPending}
            shareUrl={board.shareUrl}
            onIssueShare={handleIssueShare}
            onRevokeShare={handleRevokeShare}
            issuingShare={issueShareMutation.isPending}
            revokingShare={revokeShareMutation.isPending}
            t={t}
          />
          {!selectionInViewMonth && startDateTime ? (
            <RsBoardSelectionOutsideMonth
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              onJump={() => goToLocalDateTime(startDateTime)}
              t={t}
            />
          ) : null}
          <RsCalendarGrid
            board={board}
            year={year}
            month={month}
            today={today}
            daysInMonth={daysInMonth}
            holidayByDay={holidayByDay}
            bookingSpanGroups={bookingSpanGroups}
            gridStyle={gridStyle}
            isBoardOwner={isBoardOwner}
            isLoading={bookingsQuery.isLoading}
            resourceUpdatePending={updateResourceMutation.isPending}
            deletePending={deleteMutation.isPending}
            selectedDay={selectedDay}
            selectedResourceNames={selectedResourceNames}
            draftStartDateTime={showCalendarDraft ? startDateTime : ''}
            draftEndDateTime={showCalendarDraft ? endDateTime : ''}
            draftDisplayName={displayName}
            draftOverlapResourceNames={draftOverlapResourceNames}
            onPickSlot={handlePickCalendarSlot}
            onPrefillAfterBooking={handlePrefillAfterBooking}
            onEditResource={openResourceEdit}
            onRemoveResource={removeResource}
            onAddResource={addResource}
            onDeleteBooking={requestDeleteBooking}
            t={t}
          />
        </div>

        <aside className={styles.sidebar} data-rs-sidebar>
          <RsBookingSidebarForm
            board={board}
            displayName={displayName}
            onDisplayNameChange={setDisplayName}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            onStartDateTimeChange={setStartWithDraft}
            onEndDateTimeChange={setEndWithDraft}
            selectedResourceNames={selectedResourceNames}
            onSelectedResourceNamesChange={(next) => {
              setResourcesWithDraft(next);
              persistFormPrefs({ resourceNames: next });
            }}
            notes={notes}
            onNotesChange={setNotes}
            deletePassword={deletePassword}
            onDeletePasswordChange={setDeletePassword}
            onPersistFormPrefs={() => persistFormPrefs()}
            onSubmit={() => createMutation.mutate()}
            onValidationError={flashError}
            isSubmitting={createMutation.isPending}
            overlapResourceNames={draftOverlapResourceNames}
            viewYear={year}
            viewMonth={month}
            onRevealSelectionMonth={revealSelectionMonth}
            locale={i18n.language}
            t={t}
          />
        </aside>
      </div>

      <RsBoardCalendarModals
        showResourceEdit={editResourceIndex !== null}
        editResourceName={editResourceName}
        onEditResourceNameChange={setEditResourceName}
        onCloseResourceEdit={() => {
          if (!updateResourceMutation.isPending) {
            setEditResourceIndex(null);
            setEditResourceName('');
          }
        }}
        onSubmitResourceEdit={submitResourceEdit}
        resourceEditPending={updateResourceMutation.isPending}
        deleteTarget={deleteTarget}
        deletePasswordInput={deletePasswordInput}
        onDeletePasswordInputChange={(value) => {
          setDeletePasswordInput(value);
          setDeleteErrorMessage(null);
        }}
        onCloseDeleteModal={() => {
          if (!deleteMutation.isPending) {
            setDeleteTarget(null);
            setDeletePasswordInput('');
            setDeleteErrorMessage(null);
          }
        }}
        onSubmitDelete={submitDeleteBooking}
        deletePending={deleteMutation.isPending}
        deleteErrorMessage={deleteErrorMessage}
        showOverlapModal={showOverlapModal}
        onCloseOverlapModal={() => setShowOverlapModal(false)}
      />
    </Container>
  );
}
